import {
  AppShell as DesignSystemAppShell,
  SelectField,
  BlobCtaButton,
  GlassChip,
  LoadingSpinner,
  ThemeBackground,
  Typography,
  type SelectFieldOption,
  type ShellNavStructure,
} from "@cupcodev/ui";
import { useEffect, useMemo, useRef, useState, type ReactNode, type ChangeEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAccountsExperience } from "../auth/AccountsExperienceProvider";
import { useI18n } from "../i18n/I18nProvider";
import { useScenario } from "../scenario/ScenarioProvider";
import { telemetry } from "../telemetry/telemetry";
import { naveBrandAssets, resolveNaveAvatar } from "../branding/telescup-brand-assets";
import { NaveIcon } from "../../ui/NavePage";
import { UpgradeOfferCard } from "./UpgradeOfferCard";
import { ProfileCompletionProvider } from "./CompleteProfilePrompt";
import { ADS_DEMO_ENABLED } from "./ads-demo";

export const AppShell = ({ children }: { children?: ReactNode }) => {
  const { t, locale, setLocale } = useI18n();
  const { logout } = useAccountsExperience();

  const {
    runtime,
    activeOrganizationId,
    setOrganization,
    session, switching, switchError, setClient,
  } = useScenario();

  const location = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const previousPathRef = useRef(location.pathname);

  const organizations = runtime.scenario.organizations;
  const [organizationSearch, setOrganizationSearch] = useState("");
  const [clientSearch,setClientSearch]=useState("");
  const platformAdmin = session?.platform?.isPlatformAdmin === true;
  const homeOrganizationId = session?.platform?.homeOrganizationId;
  const viewingCustomer = platformAdmin && (session?.activeClient !== undefined || (homeOrganizationId && homeOrganizationId !== activeOrganizationId.value));
  const selectedOrganization = organizations.find((item) => item.organizationId.value === activeOrganizationId.value);


  const displayName =
    runtime.scenario.activeUser.displayName ??
    runtime.scenario.activeUser.email ??
    t("app.name");
  const avatarUrl = typeof session?.actor.avatarRef === "string"
    ? resolveNaveAvatar(session.actor.avatarRef) : undefined;

  useEffect(() => {
    const content = contentRef.current;
    if (content === null) return;
    const followInternalLink = (event: MouseEvent): void => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[href]") : null;
      if (link === null || link.hasAttribute("download") || (link.target !== "" && link.target !== "_self")) return;
      const destination = new URL(link.href);
      if (destination.origin !== window.location.origin || !["http:", "https:"].includes(destination.protocol)) return;
      if (destination.pathname.startsWith("/api/") || destination.pathname.startsWith("/auth/")) return;
      event.preventDefault();
      navigate(`${destination.pathname}${destination.search}${destination.hash}`);
    };
    content.addEventListener("click", followInternalLink);
    return () => content.removeEventListener("click", followInternalLink);
  }, [navigate]);


  useEffect(() => {
    const previousPath = previousPathRef.current;
    previousPathRef.current = location.pathname;
    if (previousPath === location.pathname) return;

    const main = contentRef.current?.closest<HTMLElement>("main");
    if (main === undefined || main === null) return;
    if (!main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
    main.focus();
  }, [location.pathname]);
  useEffect(() => {
    telemetry.emit({
      name: "route.viewed",
      outcome: "success",
      attributes: {
        route: location.pathname === "/" ? "home" : location.pathname,
      },
    });
  }, [location.pathname]);

  const organizationOptions =
    organizations.filter((organization) => {
      const customerNames = session?.organizations.find((item) => item.id === organization.organizationId.value)?.customerNames ?? [];
      return organization.organizationId.value === activeOrganizationId.value
        || organization.name.toLocaleLowerCase(locale).includes(organizationSearch.toLocaleLowerCase(locale))
        || (platformAdmin && customerNames.some((name) => name.toLocaleLowerCase(locale).includes(organizationSearch.toLocaleLowerCase(locale))));
    }).map<SelectFieldOption<string>>((organization) => {
      const customerNames = session?.organizations.find((item) => item.id === organization.organizationId.value)?.customerNames ?? [];
      return {
      value: organization.organizationId.value,
      label: platformAdmin && customerNames.length === 1 ? `${organization.name} · ${customerNames[0]}` : organization.name,
    }; });

  // Home and the inbox stay top-level; everything else is grouped by purpose.
  // `exclusiveGroups` (opening a group closes the previous one) needs a @cupcodev/ui
  // release that supports it; earlier versions ignore the flag, so it is spread in to
  // keep the build valid against either version's published types.
  const accordion = { exclusiveGroups: true };
  // The ads screen is only offered to customers that actually hired a paid-traffic service.
  const [hasAdsService, setHasAdsService] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setHasAdsService(false);
    if (!ADS_DEMO_ENABLED || !session?.activeClient || !runtime.experience.readAdsAnalytics) return;
    void runtime.experience.listServices().then((result) => {
      if (!cancelled && result.ok) setHasAdsService(result.value.items.some((service) => /tr[aá]fego|an[uú]ncio|\bads\b/iu.test(service.name)));
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [runtime, session?.activeClient?.id]);

  // Unread count for the inbox badge; refreshed on every navigation so opening a notification updates it.
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let cancelled = false;
    if (!session?.activeClient || !runtime.experience.readInbox) { setUnread(0); return; }
    void runtime.experience.readInbox().then((result) => {
      if (!cancelled && result.ok) setUnread(result.value.notifications.filter((item) => !item.read).length);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [runtime, session?.activeClient?.id, location.pathname]);

  const navStructure = useMemo<ShellNavStructure>(
    () => ({
      items: [
        { id: "home", label: t("navigation.home"), icon: "lucide:house", href: "/" },
        ...(hasAdsService ? [{ id: "ads", label: t("navigation.adsDemo"), icon: "lucide:chart-no-axes-combined", href: "/ads" }] : []),
        { id: "notifications", label: t("notifications.title"), icon: "lucide:bell", href: "/notifications",
          // `badge` needs a @cupcodev/ui release that renders it; spread so older types still build.
          ...(unread > 0 ? { badge: unread, badgeLabel: t("notifications.unreadCount", { count: unread }) } : {}) },
      ],
      groups: [
        { id: "work", label: t("navigation.group.work"), icon: "lucide:briefcase-business", items: [
          { id: "projects", label: t("navigation.projectsServices"), icon: "lucide:layout-grid", href: "/projects" },
          { id: "approvals", label: t("approvals.title"), icon: "lucide:badge-check", href: "/approvals" },
          { id: "requests", label: t("collaboration.requests.title"), icon: "lucide:message-square-more", href: "/requests" },
          { id: "decisions", label: t("decisions.listTitle"), icon: "lucide:split", href: "/decisions" },
          { id: "deliverables", label: t("collaboration.deliverables.listTitle"), icon: "lucide:package-check", href: "/deliverables" },
        ] },
        { id: "commercial", label: t("navigation.group.commercial"), icon: "lucide:wallet", items: [
          { id: "billing", label: t("navigation.billing"), icon: "lucide:receipt-text", href: "/billing" },
          { id: "documents", label: t("navigation.documents"), icon: "lucide:file-text", href: "/documents" },
          { id: "questionnaires", label: t("questionnaires.title"), icon: "lucide:clipboard-list", href: "/questionnaires" },
        ] },
        { id: "service", label: t("navigation.group.service"), icon: "lucide:life-buoy", items: [
          { id: "agenda", label: t("agenda.title"), icon: "lucide:calendar-days", href: "/agenda" },
          { id: "support", label: t("navigation.support"), icon: "lucide:headset", href: "/support" },
          { id: "help", label: t("help.title"), icon: "lucide:circle-help", href: "/help" },
        ] },
      ],
      ...accordion,
    }),
    [t, hasAdsService, unread],
  );

  const activeId = resolveActiveNavigation(location.pathname);
  const hrefFor = (item: { id: string; href?: string }): string => item.href ?? "/";

  return (
    <>
      <ThemeBackground enableStars />

      <ProfileCompletionProvider>
      <DesignSystemAppShell
        brand={{
          name: "Nave",
          wordmarkSrc: naveBrandAssets.horizontalLogo,
          wordmarkAlt: "Nave · Cupcode",
          href: "/",
          onClick: (event) => { event.preventDefault(); navigate("/"); },
        }}
        {...{ sidebarFooter: <UpgradeOfferCard /> }}
        navStructure={navStructure}
        activeId={activeId}
        getItemHref={hrefFor}
        onNavigate={(item) => navigate(hrefFor(item))}
        mainLayout="content"
        header={{
          notifications: [],
          userMenu: {
            displayName,
            ...(avatarUrl ? { avatarUrl } : {}),
            email:
              runtime.scenario.activeUser.email ??
              t("user.emailUnavailable"),
            appVersion: "0.1.0",
            onLogout: logout,
            notifications: [],
            ...(platformAdmin ? { roleLabel: t("platform.admin") } : {}),
            language: locale === "en" ? "en-US" : locale === "es" ? "es-ES" : "pt-BR",
            onLanguageChange: (next) => setLocale(next === "en-US" ? "en" : next === "es-ES" ? "es" : "pt-BR"),
          },
        }}
      >
        <div className="nave-context-strip">
          {viewingCustomer ? <div className="nave-admin-context" role="status">
            <NaveIcon name="lucide:eye" />
            <Typography as="span" variant="body-sm">{t("platform.viewing", { organization: session?.activeClient?.name ?? selectedOrganization?.name ?? "" })}</Typography>
            <BlobCtaButton size="sm" variant="secondary" disabled={switching === true} onClick={() => {
              if(session?.activeClient && setClient) {setClient("");return;}
              const home = organizations.find((item) => item.organizationId.value === homeOrganizationId);
              if (home) { setOrganizationSearch(""); setOrganization(home.organizationId); }
            }}>{t("platform.return")}</BlobCtaButton>
          </div> : null}

          {session?.clients && setClient ? <div className="nave-context-field" aria-label={t("client.label")}>
            <span className="nave-context-label"><NaveIcon name="lucide:building-2" />{t("client.label")}</span>
            {platformAdmin ? <label className="nave-organization-search"><span className="sr-only">{t("client.search")}</span>
              <NaveIcon name="lucide:search" />
              <input type="search" value={clientSearch} placeholder={t("client.search")} onChange={(event:ChangeEvent<HTMLInputElement>)=>setClientSearch(event.target.value)} disabled={switching===true}/></label> : null}
            {!platformAdmin && session.clients.length === 1 ? <Typography as="span" variant="body-md" className="nave-context-value">{session.clients[0]?.name}</Typography> : <SelectField value={session.activeClient?.id ?? ""} ariaLabel={t("client.label")} disabled={switching === true}
              triggerClassName="nave-select-trigger" onChange={setClient}
              options={[{value:"",label:t("client.choose")},...session.clients.filter(c=>!platformAdmin || c.id===session.activeClient?.id || c.name.toLocaleLowerCase(locale).includes(clientSearch.toLocaleLowerCase(locale))).map(c=>({value:c.id,label:c.isTest ? `${c.name} · ${t("client.test")}`:c.name}))]} />}
            {session.activeClient?.isTest ? <GlassChip variant="warning">{t("client.test")}</GlassChip>:null}
          </div>:null}

          {platformAdmin || !session ? <div className="nave-context-field" aria-label={t("organization.label")}>
            <span className="nave-context-label"><NaveIcon name="lucide:building" />{organizations.length > 1 ? t("organization.switch") : t("organization.single")}</span>
            {platformAdmin ? <label className="nave-organization-search">
              <span className="sr-only">{t("platform.search")}</span>
              <NaveIcon name="lucide:search" />
              <input type="search" value={organizationSearch} placeholder={t("platform.search")}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setOrganizationSearch(event.target.value)} disabled={switching} />
            </label> : null}
            <SelectField
              value={activeOrganizationId.value}
              onChange={(nextValue) => {
                const organization = organizations.find(
                  (item) =>
                    item.organizationId.value === nextValue,
                );

                if (organization !== undefined) {
                  setOrganization(organization.organizationId);
                }
              }}
              options={organizationOptions}
              disabled={switching || organizations.length <= 1}
              ariaLabel={t("organization.label")}
              triggerClassName="nave-select-trigger"
            />
          </div> : null}
        </div>
        {switchError ? <div className="nave-switch-error" role="alert"><NaveIcon name="lucide:triangle-alert" />{t("organization.switchFailed")}</div> : null}

        <div ref={contentRef} className="nave-shell-content">
          {switching ? <div role="status" aria-busy="true"><LoadingSpinner label={t("state.loading")} /></div> : children}
        </div>
      </DesignSystemAppShell>
      </ProfileCompletionProvider>
    </>
  );
};

const resolveActiveNavigation = (pathname: string): string => {
  if (pathname === "/") return "home";
  if (pathname.startsWith("/ads")) return "ads";
  if (pathname.startsWith("/agenda")) return "agenda";
  if (pathname.startsWith("/approvals")) return "approvals";
  if (pathname.startsWith("/requests")) return "requests";
  if (pathname.startsWith("/notifications")) return "notifications";
  if (pathname.startsWith("/decisions")) return "decisions";
  if (pathname.startsWith("/deliverables")) return "deliverables";
  if (pathname.startsWith("/help")) return "help";
  if (/^\/(projects|services)(\/|$)/u.test(pathname)) return "projects";
  if (pathname.startsWith("/billing")) return "billing";
  if (pathname.startsWith("/questionnaires")) return "questionnaires";
  if (/^\/documents(\/|$)/u.test(pathname)) return "documents";
  if (pathname.startsWith("/support")) return "support";
  return "";
};
